import {
  isAnyBuyAndSellCategory,
  isAnyCarsVehiclesCategory,
  isBuyAndSellCategory,
} from '@kijiji/category'
import {
  GetPendingSearchInputDocument,
  GetUserLocationDocument,
  ResultsView,
} from '@kijiji/generated/graphql-types'
import { type GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useMemo, useState } from 'react'
import { AdvertisingSlot } from 'react-advertising'
import { useTheme } from 'styled-components'

import { PopularMakesCarousel } from '@/components/brp/PopularMakesCarousel'
import { FindCar } from '@/components/brp/top-cars-banner/find-car/FindCar'
import { HideAtOrLarger, ShowAtOrLarger } from '@/components/shared/breakpoint'
import { ErrorBoundary } from '@/components/shared/error-boundary'
import { BaseLayout } from '@/components/shared/layouts/BaseLayout'
import { PageContainer } from '@/components/shared/page-container'
import { RadiusBoostSystemMessage } from '@/components/shared/radius-boost-system-message/RadiusBoostSystemMessage'
import { SrpGrid, SrpHeader, SrpResultsSort } from '@/components/srp'
import { FiltersSidebar } from '@/components/srp/filters/FiltersSidebar'
import { SearchFloatingButtons } from '@/components/srp/search-floating-buttons'
import { SearchList } from '@/components/srp/search-list'
import { SrpBreadcrumb } from '@/components/srp/srp-breadcrumb/SrpBreadcrumb'
import { SrpError } from '@/components/srp/srp-error'
import { LoadingSrpHeader } from '@/components/srp/srp-loading/LoadingSrpHeader'
import { AdSenseSrpContainer } from '@/components-page/srp/advertisement/AdsenseSrpContainer'
import { BRPTopCarsTrucks } from '@/components-page/srp/advertisement/brp-top-cars-trucks/BrpTopCarsTrucks'
import {
  SearchListingsWrapper,
  SkyscraperUpperRightAdSlotContainer,
} from '@/components-page/srp/advertisement/styled'
import { TopLeaderboardAdSlot } from '@/components-page/srp/advertisement/TopLeaderboardAdSlot'
import { SearchAdsWrapper } from '@/components-page/vip/advertisement/SearchAdsWrapper'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { getSearchCategoryFromSearchQuery } from '@/domain/category/getSearchCategoryFromSearchQuery'
import { debugServerTime } from '@/domain/debugServerTiming'
import { initializePageServer } from '@/domain/initializePageServer'
import { getSrpComposedUrl } from '@/domain/srp/getSrpComposedUrl'
import { getSrpContentfulData } from '@/domain/srp/getSrpContentfulData'
import {
  type GetSrpPropsWithCache,
  type PageRedirect,
  getSrpLoadData,
} from '@/domain/srp/getSrpLoadData'
import { redirectLocationlessUrl } from '@/domain/srp/page-redirects/redirectLocationlessUrl'
import ClarivoyScript from '@/features/advertisement/components/clarivoy/clarivoyScript'
import useClarivoy from '@/features/advertisement/components/clarivoy/useClarivoy'
import { GPT_COMPONENT_ID } from '@/features/advertisement/constants/adSlots'
import { QuickFilters } from '@/features/filters/components/QuickFilters/QuickFilters'
import { useFavouriteOnLoginCallback } from '@/features/listing/hooks/useFavouriteOnLoginCallback'
import { MapProvider } from '@/features/map/components/MapProvider'
import { MapContainer } from '@/features/map/components/styled'
import { getMenuPrefetch } from '@/features/navigation/api/getMenuPrefetch'
import { useGetSrpState } from '@/features/search/hooks/useGetSrpState'
import { ProductCarouselSeoJsonLd } from '@/features/seo/components/ProductCarouselSeoJsonLd'
import { SeoMetadata } from '@/features/seo/components/SeoMetadata'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { useAutoBrp } from '@/hooks/srp/useAutoBrp'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useSearchLoadingState } from '@/hooks/srp/useSearchLoadingState'
import { useExtendedSearch } from '@/hooks/useExtendedSearch'
import { useLocale } from '@/hooks/useLocale'
import { useSaveSearchFromLogin } from '@/hooks/useSaveSearchFromLogin'
import { RemoteParamKeys } from '@/lib/firebase/config'
import { useExperiment, useToggle } from '@/lib/firebase/hooks'
import { GA_PAGE_TYPE } from '@/lib/ga/constants/datalayer'
import { useSrpTracking } from '@/lib/ga/hooks/useSrpTracking'
import profile from '@/lib/metrics/profileWrapper'
import { type BasePageProps } from '@/pages/_app'
import { type IntlResponse, isIntlResponse } from '@/types/translations'
import { Flex } from '@/ui/atoms/flex'
import { Spacing } from '@/ui/atoms/spacing'
import { getUserDevice } from '@/utils/getUserDevice'
import { sendToLogger } from '@/utils/sendToLogger'

const BaseLeaderboardAdSlot = dynamic(
  () =>
    import('@/components-page/srp/advertisement/BaseLeaderboardAdSlot').then(
      (mod) => mod.BaseLeaderboardAdSlot
    ),
  { ssr: false }
)

const ContentfulContent = dynamic(
  () =>
    import('@/components/shared/contentful-content/ContentfulContent').then(
      (mod) => mod.ContentfulContent
    ),
  { ssr: false }
)

const MapSRP = dynamic(() => import('@/features/map').then((mod) => mod.MapSRP), {
  ssr: false,
})

export type SRPErrors = {
  hasServerError?: boolean
  hasResultsError?: boolean
}

export interface SearchPageServerProps extends BasePageProps {
  /**
   * Error states from SRP Anvil calls
   */
  errors?: SRPErrors
  /**
   * An ID that is used to identify the page load. This allows us to detect new
   * page loads where the url hasn't changed.
   */
  pageLoadId: string
}

const SearchPage = ({
  pageLoadId,
  userAgent,
  errors: serverErrors,
  ...basePageProps
}: SearchPageServerProps) => {
  const { setLoadingStates } = useSearchLoadingState()
  const { currentPage, viewType } = useGetSrpState()
  const isMapView = viewType === ResultsView.Map

  const [hasClientFetchAttemptError, setHasClientFetchAttemptError] = useState(false)
  const { t } = useTranslation(['srp', 'filters'])

  const { query } = useRouter()
  const { apiLocale, routeLocale } = useLocale()

  const srpUrl = getSrpComposedUrl(query)

  const {
    data: srpData,
    loading: srpLoading,
    called: srpCalled,
    client,
    loadingFilters,
  } = useGetSearchResultsData({
    fetchPolicy: 'cache-first',
    onError: () => {
      setHasClientFetchAttemptError(true)
      setLoadingStates(false)

      // errored from network request, not the cache
      client.writeQuery({
        query: GetPendingSearchInputDocument,
        data: { srp: { pendingSearchInput: null } },
      })
    },
    skip: hasClientFetchAttemptError,
  })

  const isInitialLoad = !srpData && srpLoading

  const { searchQuery, results, pagination } = srpData || {}

  const category = getSearchCategoryFromSearchQuery(searchQuery?.category, apiLocale)

  const totalPageCount = pagination ? Math.ceil(pagination.totalCount / pagination.limit) : 1
  const listings = results?.mainListings || []
  const clusters = results?.clusters || []
  const topListings = results?.topListings || []

  const textJsonPromise = useMemo(() => {
    return getSrpContentfulData({ languageKey: routeLocale, url: srpUrl })
  }, [routeLocale, srpUrl])

  const errors = {
    hasServerError: !!serverErrors?.hasServerError,
    hasResultsError: srpCalled && !srpLoading && srpData?.results === null,
  }

  useFavouriteOnLoginCallback()
  useSaveSearchFromLogin({ searchString: srpData?.searchQuery.searchString })
  useSrpTracking()

  const { colors, spacing } = useTheme()

  const { extendedListings } = useExtendedSearch()
  const { location: userLocation } = useGetLocation()
  // Check if the user location is a region (search all of X) or a location with a radius defined
  const isLocationWithRadius = useMemo(
    () => !userLocation.isRegion && !!userLocation.area && !!userLocation.area.radius,
    [userLocation.area, userLocation.isRegion]
  )

  const { isScriptLoadable: isClarivoyScriptLoadable } = useClarivoy({
    listings,
    category,
    pageType: GA_PAGE_TYPE.SEARCH_RESULTS_PAGE,
  })

  /**
   * The isMobile check should only be used when the condition explicitly needs the device type
   * Always use Media Queries and CSS when dealing with UI responsiveness
   */
  const isMobileForAds = getUserDevice(userAgent).isPhoneOrTablet

  /**
   * Toggles used in the root:
   *
   * - REMOVE_ADSENSE_MWEB
   * - EXTENDED_SEARCH_FOR_NULL_AND_LOW_SRP
   */
  // Toggle off AdSense Top placement on mWeb for Autos and Buy and Sell
  const isRemoveAdsenseMwebToggleEnabled = !!useToggle(RemoteParamKeys.REMOVE_ADSENSE_MWEB)?.enabled
  const isNewZSRPToggle =
    !!useToggle(RemoteParamKeys.EXTENDED_SEARCH_FOR_NULL_AND_LOW_SRP)?.enabled &&
    totalPageCount === 0

  const experiment = useExperiment(RemoteParamKeys.SRP_AD_UNIT)
  const srpAdUnitExperimentGroup = experiment?.group

  const shouldHideAdsenseMwebToggle =
    isRemoveAdsenseMwebToggleEnabled &&
    (isAnyBuyAndSellCategory(category.id) || isAnyCarsVehiclesCategory(category.id))

  const renderSearchListings = (searchTitle?: string | IntlResponse) => {
    if (errors.hasResultsError) {
      return <SrpError />
    }

    const h1Text =
      searchTitle && isIntlResponse(searchTitle)
        ? t(searchTitle.intl, { ...searchTitle.params })
        : searchTitle

    return (
      <SearchList
        searchTitle={h1Text}
        extendedRadiusListings={extendedListings}
        totalPageCount={totalPageCount}
      />
    )
  }

  const renderAdsenseContainer = () => {
    if (isMapView) return null

    /**
     * It should hide top adsense when the user is on the redesigned ZSRP, OR
     * When the "remove adsense" experiment is toggled on for buy & sell and autos categories
     */
    if (shouldHideAdsenseMwebToggle || isNewZSRPToggle) {
      return (
        <ShowAtOrLarger breakpoint="medium">
          <AdSenseSrpContainer id="AFSTop" />
        </ShowAtOrLarger>
      )
    }

    return <AdSenseSrpContainer id="AFSTop" />
  }

  const hasRefetchUrlError = false

  const shouldDisplayPageNumber = currentPage > 1

  const { isAutoBrp: isAutoBRPView } = useAutoBrp({
    categoryId: category.id,
    keywords: srpData?.searchQuery.keywords,
    filters: srpData?.searchQuery.filters,
  })

  const canShowRightHandAd = !isMobileForAds && !isMapView && srpAdUnitExperimentGroup === 1

  return (
    <>
      {isClarivoyScriptLoadable && <ClarivoyScript defer />}

      {isBuyAndSellCategory(category.id) && (
        <ProductCarouselSeoJsonLd id="product-carousel-srp" listings={listings} />
      )}

      <SeoMetadata pageLoadId={pageLoadId} basePageProps={basePageProps}>
        {(pageH1) => {
          const h1Text = isIntlResponse(pageH1) ? t(pageH1.intl, { ...pageH1.params }) : pageH1
          const h1TextAddition = shouldDisplayPageNumber
            ? t('header.results.title.page_number', { pageNumber: currentPage })
            : ''
          const pageH1Heading = `${h1Text}${h1TextAddition ? ` ${h1TextAddition}` : ''}`

          const content = (
            <BaseLayout
              showSearchBar={true}
              withAuthModal={true}
              includeRUMScripts={true}
              categoryId={category.id}
              viewType={viewType}
              bodyColor={colors.white}
            >
              <ErrorBoundary fingerprintId="SRP">
                {/* If there is server-side search or client-side refetch url error */}
                {hasClientFetchAttemptError || hasRefetchUrlError ? (
                  <SrpError />
                ) : (
                  <>
                    {viewType !== ResultsView.Map && (
                      <TopLeaderboardAdSlot isMobileForAds={isMobileForAds} />
                    )}

                    <PageContainer
                      bottom={isMapView ? '0' : spacing.default}
                      isFullMax={!!isMapView}
                      top="0"
                    >
                      {isAutoBRPView && isInitialLoad && <LoadingSrpHeader />}
                      {isAutoBRPView && !isInitialLoad && (
                        <Spacing mTop={spacing.xLarge} mBottom={spacing.default}>
                          <SrpBreadcrumb pageH1Heading={pageH1Heading} isAutoBRPView />
                        </Spacing>
                      )}
                      {isAutoBRPView && (
                        <BRPTopCarsTrucks>
                          <FindCar srpData={srpData} />
                        </BRPTopCarsTrucks>
                      )}
                      {isAutoBRPView && (
                        <>
                          <QuickFilters buttonTextCustomStyle="font-style: oblique;" />
                          <Spacing
                            mTop={spacing.giant}
                            mBottom={spacing.giant}
                            medium={{ mBottom: '8rem' }}
                          >
                            <PopularMakesCarousel />
                          </Spacing>
                        </>
                      )}

                      <SrpHeader
                        h1={pageH1}
                        currentPage={currentPage}
                        loading={isInitialLoad}
                        currentView={viewType}
                        categoryId={category.id}
                        isRadialSearch={isLocationWithRadius}
                        isAutoBRPView={isAutoBRPView}
                      />

                      {isMapView && (
                        <HideAtOrLarger breakpoint="medium">
                          <Flex justifyContent="flex-end">
                            <SrpResultsSort categoryId={category.id} showViewToggleOnly />
                          </Flex>
                        </HideAtOrLarger>
                      )}
                    </PageContainer>

                    <PageContainer
                      bottom={spacing.default}
                      top={{ small: '0', medium: isMapView ? '0' : spacing.default }}
                      isFullMax={isMapView}
                      removeMobilePadding={isMapView}
                    >
                      <SrpGrid shouldRenderMapSRP={isMapView}>
                        {isMapView ? (
                          <MapProvider>
                            {/*
                             * Bringing the MapContainer outside the component as the MapSRP is lazy loaded
                             * and we should have the wrapper loaded to prevent layout shift
                             */}
                            <MapContainer>
                              <MapSRP
                                listings={listings}
                                topListings={topListings}
                                clusters={clusters}
                                currentPage={currentPage}
                              />
                            </MapContainer>
                          </MapProvider>
                        ) : (
                          <FiltersSidebar
                            isMobileForAds={isMobileForAds}
                            isLoadingFilters={loadingFilters}
                            categoryId={category.id}
                          />
                        )}

                        <Flex flexDirection="column" gap={spacing.default}>
                          {/**
                           * On MAP view the sort row should only appear on desktop when there are results
                           */}
                          {isMapView && totalPageCount > 0 ? (
                            <ShowAtOrLarger breakpoint="large">
                              <SrpResultsSort categoryId={category.id} />
                            </ShowAtOrLarger>
                          ) : totalPageCount > 0 ? (
                            <SrpResultsSort categoryId={category.id} />
                          ) : null}

                          <RadiusBoostSystemMessage />

                          {renderAdsenseContainer()}

                          <Flex>
                            <SearchListingsWrapper canShowRightHandAd={canShowRightHandAd}>
                              {renderSearchListings(pageH1)}
                            </SearchListingsWrapper>
                            {canShowRightHandAd && (
                              <SkyscraperUpperRightAdSlotContainer>
                                <AdvertisingSlot
                                  id={GPT_COMPONENT_ID.SKYSCRAPER_UPPER_RIGHT}
                                  data-testid={GPT_COMPONENT_ID.SKYSCRAPER_UPPER_RIGHT}
                                />
                              </SkyscraperUpperRightAdSlotContainer>
                            )}
                          </Flex>

                          {!isMapView && <BaseLeaderboardAdSlot />}
                        </Flex>
                      </SrpGrid>

                      {textJsonPromise && (
                        <ErrorBoundary fingerprintId="ContentfulContent">
                          <ContentfulContent userAgent={userAgent} textJson={textJsonPromise} />
                        </ErrorBoundary>
                      )}

                      {isMapView ? (
                        <ShowAtOrLarger breakpoint="medium">
                          <SearchFloatingButtons isMobileForAds={isMobileForAds} />
                        </ShowAtOrLarger>
                      ) : (
                        <SearchFloatingButtons isMobileForAds={isMobileForAds} />
                      )}
                    </PageContainer>
                  </>
                )}
              </ErrorBoundary>
            </BaseLayout>
          )

          return <SearchAdsWrapper userAgent={userAgent}>{content}</SearchAdsWrapper>
        }}
      </SeoMetadata>
    </>
  )
}

const isRedirectUrl = (redirect: GetSrpPropsWithCache): redirect is PageRedirect =>
  !!(redirect as PageRedirect).redirect

export const getServerSideProps: GetServerSideProps<SearchPageServerProps> = profile<
  GetServerSideProps<SearchPageServerProps>
>('srp', 'getServerSideProps', async (context) => {
  const gSSPstart = performance.now()
  const { query, req, res } = context

  const {
    apolloClient,
    forwardedHeaders,
    basePageProps,
    locale: { cookieLocale, apiLocale },
  } = await initializePageServer('srp', context, [
    TRANSLATION_KEYS.SRP,
    TRANSLATION_KEYS.LISTING,
    TRANSLATION_KEYS.FEEDBACK,
    TRANSLATION_KEYS.FAVOURITES,
  ])

  const url = getSrpComposedUrl(query)

  /**
   * Queries we want to pre-fetch on the server to load asap in the page (use for the NAVIGATION_SRP experiment)
   * */
  const location = apolloClient.readQuery({ query: GetUserLocationDocument })
  const locationId = location?.userLocation.id

  /**
   * When the URL doesn't contain the location information
   * then use the user preferences to execute the SRP/BRP search
   * AND url gets updated to match the location search
   */
  const locationlessRedirect = await profile(
    'srp',
    'redirectLocationlessUrl',
    redirectLocationlessUrl
  )({ url, cookies: req.cookies })

  if (locationlessRedirect) {
    return { redirect: locationlessRedirect.redirect }
  }

  // Fetch the Firebase decision for the NAVIGATION_SRP experiment
  try {
    if (basePageProps.machId) {
      const navigationSrpDecision =
        basePageProps?.firebaseConfig?.toggles[RemoteParamKeys.NAVIGATION_SRP].enabled ?? false

      if (navigationSrpDecision) {
        await profile(
          'srp',
          'getMenuPrefetch',
          getMenuPrefetch
        )({ apolloClient, cookieLocale, locationId })
      }
    }
  } catch (err) {
    sendToLogger(err, { fingerprint: ['getMenuPrefetchSrp'] })
  }

  const srpPropsOrRedirect = await profile(
    'srp',
    'getSrpLoadData',
    getSrpLoadData
  )({
    queryParameters: query,
    apolloClient,
    basePageProps,
    requestHeaders: { headers: forwardedHeaders, cookies: req.cookies },
    url,
    apiLocale,
  })

  if (isRedirectUrl(srpPropsOrRedirect)) {
    return { redirect: srpPropsOrRedirect.redirect }
  }

  debugServerTime('props', res, gSSPstart)

  return { props: srpPropsOrRedirect }
})

export default SearchPage
